import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ReactLenis } from "lenis/react";
import {
  PhoneCall,
  MessageSquare,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Send,
  CheckCheck,
  Signal,
  Wifi,
  BatteryFull,
  X as XIcon,
  Phone,
  MicOff,
  Volume2,
} from "lucide-react";
import Navbar from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ARJUNA_IMG = `${API}/static/sales/arjuna.jpg`;
const PHONE_RAW = "+919699167402";
const PHONE_DISPLAY = "+91 96991 67402";

export default function Sales() {
  const [mode, setMode] = useState(null); // null | 'call' | 'chat'

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="relative min-h-screen bg-arjuna-ink" data-testid="sales-page">
        <Navbar />

        <section className="relative overflow-hidden pt-28 pb-12">
          <div className="pointer-events-none absolute inset-0 opacity-80 [background:radial-gradient(55%_55%_at_20%_30%,rgba(0,229,255,0.14),transparent_60%),radial-gradient(55%_55%_at_80%_30%,rgba(59,130,246,0.14),transparent_60%)]" />
          <div className="container-x relative">
            <Link
              to="/"
              data-testid="sales-back"
              className="mb-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400 transition-colors hover:text-arjuna-teal"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
            </Link>

            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              {/* Portrait */}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="lg:col-span-6"
              >
                <div className="relative mx-auto max-w-xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] shadow-[0_30px_80px_-30px_rgba(0,229,255,0.4)]">
                  <div className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-br from-arjuna-teal/25 via-transparent to-blue-500/20 opacity-70 blur-2xl" />
                  <img
                    src={ARJUNA_IMG}
                    alt="Arjuna, AI sales representative"
                    className="relative w-full"
                    data-testid="arjuna-portrait"
                  />
                  <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400 backdrop-blur">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Online now
                  </div>
                </div>
              </motion.div>

              {/* Intro */}
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="lg:col-span-6"
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-arjuna-teal/30 bg-arjuna-teal/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.28em] text-arjuna-teal">
                  AI Sales · Live
                </span>
                <h1 className="mt-6 font-serif text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                  Hi, I'm <span className="text-blue-400">Arjuna.</span>
                </h1>
                <p className="mt-5 max-w-xl text-base text-slate-300 sm:text-lg">
                  Your AI sales representative at Arjuna Space. Ask me about our
                  Oceans, Forests or Agriculture products, pricing, or how to
                  pilot with your team. I can also connect you to a human
                  specialist whenever you want.
                </p>

                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setMode("call")}
                    data-testid="sales-call-btn"
                    className="group relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6 text-left transition-all duration-300 hover:border-emerald-400/60 hover:bg-emerald-500/[0.1]"
                  >
                    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-500/20 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                    <PhoneCall className="h-6 w-6 text-emerald-400" />
                    <p className="mt-4 font-serif text-2xl text-white">Call Arjuna</p>
                    <p className="mt-1 text-sm text-slate-400">
                      Speak with a human sales specialist right now.
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-400">
                      Ring the line
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setMode("chat")}
                    data-testid="sales-message-btn"
                    className="group relative overflow-hidden rounded-2xl border border-blue-500/30 bg-blue-500/[0.06] p-6 text-left transition-all duration-300 hover:border-blue-400/60 hover:bg-blue-500/[0.1]"
                  >
                    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-blue-500/20 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                    <MessageSquare className="h-6 w-6 text-blue-400" />
                    <p className="mt-4 font-serif text-2xl text-white">Message Arjuna</p>
                    <p className="mt-1 text-sm text-slate-400">
                      Text with Arjuna. Instant answers, 24×7.
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-400">
                      Start messaging
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </button>
                </div>

                <p className="mt-6 text-xs text-slate-500">
                  Prefer email? Reach us at{" "}
                  <a href="mailto:snigdha@arjunaspace.in" className="text-arjuna-teal hover:underline">
                    snigdha@arjunaspace.in
                  </a>
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <AnimatePresence>
          {mode === "chat" && <ChatSheet onClose={() => setMode(null)} />}
          {mode === "call" && <CallSheet onClose={() => setMode(null)} />}
        </AnimatePresence>
      </div>
    </ReactLenis>
  );
}

// ---------- iPhone-style Chat Sheet ----------
function ChatSheet({ onClose }) {
  const sessionId = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I'm Arjuna. Ask me anything about our ocean, forest or agriculture products. How can I help today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const r = await axios.post(`${API}/sales/chat`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Something broke on my side. Please try again in a moment." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-4 backdrop-blur-md sm:items-center"
      data-testid="chat-sheet"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, scale: 0.98, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 60, scale: 0.98, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[380px] overflow-hidden rounded-[44px] border border-white/10 bg-[#0A0F17] shadow-[0_40px_120px_-30px_rgba(0,229,255,0.4)]"
        style={{ height: "min(720px, 92vh)" }}
      >
        {/* iPhone status bar */}
        <div className="relative flex items-center justify-between bg-black px-6 pt-3 pb-2 text-[11px] font-semibold text-white">
          <span>{now}</span>
          <span className="absolute left-1/2 top-1 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
          <div className="flex items-center gap-1 text-white/90">
            <Signal className="h-3 w-3" />
            <Wifi className="h-3 w-3" />
            <BatteryFull className="h-3.5 w-3.5" />
          </div>
        </div>

        {/* Chat header */}
        <div className="flex items-center justify-between border-b border-white/5 bg-[#0B111A] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="chat-close"
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/5 hover:text-white"
            aria-label="Close chat"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="relative h-9 w-9 overflow-hidden rounded-full border border-white/10">
              <img src={ARJUNA_IMG} alt="Arjuna" className="h-full w-full object-cover" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-white">Arjuna</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 align-middle" />
                Active
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/5 hover:text-white"
            aria-label="Close"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="h-[calc(100%-180px)] overflow-y-auto px-4 py-6"
          data-testid="chat-messages"
        >
          <p className="mb-5 text-center font-mono text-[10px] uppercase tracking-widest text-slate-500">
            iMessage · Today {now}
          </p>
          <div className="space-y-2">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} />
            ))}
            {sending && (
              <div className="flex items-center gap-1 pl-1" data-testid="chat-typing">
                <TypingDot delay={0} />
                <TypingDot delay={0.15} />
                <TypingDot delay={0.3} />
              </div>
            )}
          </div>
          <p className="mt-6 flex items-center justify-center gap-1 text-right text-[10px] text-slate-500">
            <CheckCheck className="h-3 w-3 text-blue-400" /> Delivered
          </p>
        </div>

        {/* Input */}
        <form
          onSubmit={send}
          className="absolute bottom-0 left-0 right-0 flex items-center gap-2 border-t border-white/5 bg-[#0B111A] px-3 py-3"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="iMessage"
            data-testid="chat-input"
            className="flex-1 rounded-full border border-white/10 bg-black/50 px-4 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-400"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            data-testid="chat-send"
            aria-label="Send"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:bg-blue-500/40"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </form>

        {/* Home indicator */}
        <div className="absolute bottom-1 left-1/2 h-1 w-28 -translate-x-1/2 rounded-full bg-white/40" />
      </motion.div>
    </motion.div>
  );
}

function Bubble({ role, text }) {
  const mine = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25 }}
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
    >
      <div
        data-testid={`bubble-${role}`}
        className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[14px] leading-snug shadow-sm ${
          mine
            ? "rounded-br-md bg-blue-500 text-white"
            : "rounded-bl-md bg-[#2A2D32] text-slate-100"
        }`}
      >
        {text.split("\n").map((line, i) => (
          <p key={i} className={i > 0 ? "mt-1.5" : ""}>
            {line}
          </p>
        ))}
      </div>
    </motion.div>
  );
}

function TypingDot({ delay }) {
  return (
    <motion.span
      initial={{ opacity: 0.3, y: 0 }}
      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
      transition={{ duration: 1, repeat: Infinity, delay, ease: "easeInOut" }}
      className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400"
    />
  );
}

// ---------- Call Sheet ----------
function CallSheet({ onClose }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");

  // Launch native dialer immediately
  useEffect(() => {
    const t = setTimeout(() => {
      window.location.href = `tel:${PHONE_RAW}`;
    }, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/80 p-4 backdrop-blur-md sm:items-center"
      data-testid="call-sheet"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, scale: 0.98, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 60, scale: 0.98, opacity: 0 }}
        transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm overflow-hidden rounded-[44px] border border-white/10 bg-gradient-to-b from-[#0A1A2E] via-[#061322] to-[#02070E] p-8 text-center shadow-[0_40px_120px_-30px_rgba(0,229,255,0.5)]"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-emerald-400">
          <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse align-middle" />
          Calling. {mm}:{ss}
        </p>
        <div className="relative mx-auto mt-8 h-32 w-32 overflow-hidden rounded-full border-2 border-white/10">
          <img src={ARJUNA_IMG} alt="Arjuna" className="h-full w-full object-cover" />
        </div>
        {/* Ringing rings */}
        <span className="pointer-events-none absolute left-1/2 top-[122px] block h-32 w-32 -translate-x-1/2 rounded-full border border-emerald-400/50 opacity-60 animate-ping" />
        <h2 className="mt-5 font-serif text-3xl text-white">Arjuna</h2>
        <p className="mt-1 text-sm text-slate-400">AI Sales · Arjuna Space</p>
        <a
          href={`tel:${PHONE_RAW}`}
          data-testid="call-number"
          className="mt-6 inline-block font-mono text-lg tracking-wider text-arjuna-teal hover:underline"
        >
          {PHONE_DISPLAY}
        </a>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            type="button"
            className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80"
            aria-label="Mute"
          >
            <MicOff className="h-5 w-5" />
          </button>
          <a
            href={`tel:${PHONE_RAW}`}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_0_32px_rgba(34,197,94,0.6)] hover:bg-emerald-400"
            aria-label="Call"
            data-testid="call-dial"
          >
            <Phone className="h-6 w-6" />
          </a>
          <button
            type="button"
            className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80"
            aria-label="Speaker"
          >
            <Volume2 className="h-5 w-5" />
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          data-testid="call-end"
          className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(239,68,68,0.5)] hover:bg-red-400"
        >
          <Phone className="h-4 w-4 rotate-[135deg]" /> End Call
        </button>

        <p className="mt-5 text-[11px] text-slate-500">
          Didn't dial? Tap the number above or dial{" "}
          <span className="text-arjuna-teal">{PHONE_DISPLAY}</span>
        </p>
      </motion.div>
    </motion.div>
  );
}
